import { PropsWithChildren, ReactNode } from 'react';

interface ScrollablePageWrapperProps extends PropsWithChildren {
  /** The navbar element to render inside the scroll container (sticky at top) */
  navbar: ReactNode;
}

/**
 * Wraps a navbar + page content in a single scroll container so that
 * content scrolls behind the sticky navbar, enabling the glass
 * backdrop-filter blur effect.
 *
 * Mirrors the NextGenLayout pattern that already works.
 *
 * Mobile bottom padding:
 *   5rem (80px) clearance above the h-16 (64px) mobile bottom nav, leaving
 *   ~16px of visible gap so card/button edges aren't flush with the nav.
 *   Plus env(safe-area-inset-bottom), which is platform-agnostic and adds
 *   the OS-reported unsafe area on both iOS (home indicator) and Android
 *   (gesture nav bar / display cutouts). Returns 0 elsewhere. Requires
 *   viewport-fit=cover in the viewport meta tag (set in index.html).
 *   Desktop has no bottom nav, so md:pb-0.
 */
export function ScrollablePageWrapper({ navbar, children }: ScrollablePageWrapperProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Single scrollable area — sticky nav stays at top, content flows below */}
      <div className="flex-1 overflow-auto w-full">
        {navbar}
        <div className="pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0">
          {children}
        </div>
      </div>
    </div>
  );
}
